import { useState } from 'react'
import { Plus, ShieldAlert } from 'lucide-react'
import { EmptyState, ErrorState, FormDialog, PageHeader, StatusBadge, EmployeePicker } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useLookup } from '@/hooks/useLookup'
import { toApiProblem } from '@/lib/api/client'
import { usePipMutations, usePips } from '../api'
import { PIP_STATUS_LABELS } from '../types'
import type { PipStatus } from '../types'
import { Select } from '@/components/ui/select'
import { DatePicker } from '@/components/ui/date-picker'

const STATUS_TONE: Record<PipStatus, 'neutral' | 'info' | 'success' | 'warning' | 'danger'> = {
  DRAFT: 'neutral',
  ACTIVE: 'warning',
  UNDER_REVIEW: 'info',
  SUCCESSFUL: 'success',
  UNSUCCESSFUL: 'danger',
  CANCELLED: 'neutral',
}

/** Performance improvement plans. One live plan per person. */
export function PipsPage() {
  useBreadcrumb()
  const pips = usePips()
  const entities = useLookup<{ id: string; name: string }>('legalEntities')
  const { raise, activate, close } = usePipMutations()

  const [draft, setDraft] = useState<{
    legalEntityId: string; employeeId: string; reason: string; expectations: string
    supportOffered: string; startDate: string; endDate: string
  } | null>(null)
  const [closing, setClosing] = useState<{
    id: string; status: string; outcomeNote: string
  } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const act = (work: () => Promise<unknown>, fallback: string) => {
    void (async () => {
      setError(null)
      try {
        await work()
      } catch (caught) {
        setError(toApiProblem(caught)?.detail ?? fallback)
      }
    })()
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Improvement plans"
        subtitle="What is expected, over what period, and what came of it."
        actions={
          <Button
            type="button"
            onClick={() => {
              setError(null)
              setDraft({
                legalEntityId: entities.data?.[0]?.id ?? '', employeeId: '', reason: '',
                expectations: '', supportOffered: '', startDate: '', endDate: '',
              })
            }}
          >
            <Plus className="size-4" aria-hidden="true" />
            New plan
          </Button>
        }
      />

      <p className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
        <ShieldAlert className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        A plan that does not say what is expected is not a plan — it is a warning with a date
        on it, and it is the first document anybody reviewing a dismissal asks to see. Both the
        reason and the expectations are required, and closing one needs an account of what
        happened.
      </p>

      {error && (
        <p className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
          {error}
        </p>
      )}

      <Card>
        {pips.isPending && <Skeleton className="m-4 h-24" />}
        {pips.isError && <ErrorState error={pips.error} onRetry={() => void pips.refetch()} />}
        {pips.isSuccess && pips.data.length === 0 && (
          <EmptyState
            title="No plan is running"
            description="A plan sets out what is expected and by when, and records what happened at the end."
          />
        )}
        {pips.isSuccess && pips.data.length > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-slate-600">
                <tr>
                  <th className="p-3 text-start font-medium">Reference</th>
                  <th className="p-3 text-start font-medium">Person</th>
                  <th className="p-3 text-start font-medium">Period</th>
                  <th className="p-3 text-start font-medium">Expectations</th>
                  <th className="p-3 text-start font-medium">Status</th>
                  <th className="p-3 text-end font-medium" />
                </tr>
              </thead>
              <tbody>
                {pips.data.map((pip) => (
                  <tr key={pip.id} className="border-b border-slate-100 align-top">
                    <td className="p-3 font-mono">{pip.reference}</td>
                    <td className="p-3">{pip.employeeName ?? '—'}</td>
                    <td className="p-3 text-slate-600">
                      {pip.startDate} → {pip.endDate}
                    </td>
                    <td className="p-3 max-w-md text-xs text-slate-600">{pip.expectations}</td>
                    <td className="p-3">
                      <StatusBadge
                        status={pip.status}
                        tone={STATUS_TONE[pip.status as PipStatus] ?? 'neutral'}
                        label={PIP_STATUS_LABELS[pip.status as PipStatus] ?? pip.status}
                      />
                      {pip.outcomeNote && (
                        <p className="mt-1 max-w-xs text-xs text-slate-600">{pip.outcomeNote}</p>
                      )}
                    </td>
                    <td className="p-3 text-end">
                      <div className="flex justify-end gap-2">
                        {pip.status === 'DRAFT' && (
                          <Button
                            type="button"
                            onClick={() => act(
                              () => activate.mutateAsync(pip.id),
                              'That plan could not be started')}
                          >
                            Start
                          </Button>
                        )}
                        {(pip.status === 'ACTIVE' || pip.status === 'UNDER_REVIEW') && (
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                              setError(null)
                              setClosing({ id: pip.id, status: 'SUCCESSFUL', outcomeNote: '' })
                            }}
                          >
                            Close
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <FormDialog
        open={draft != null}
        onOpenChange={(open) => { if (!open) setDraft(null) }}
        size="lg"
        title="New improvement plan"
        description="Two live plans for one person are refused — nobody can satisfy two sets of expectations."
        submitLabel="Save as draft"
        loading={raise.isPending}
        error={error}
        onSubmit={() => {
          if (!draft) return
          act(async () => {
            await raise.mutateAsync({
              legalEntityId: draft.legalEntityId,
              employeeId: draft.employeeId,
              reason: draft.reason,
              expectations: draft.expectations,
              supportOffered: draft.supportOffered || null,
              startDate: draft.startDate,
              endDate: draft.endDate,
            })
            setDraft(null)
          }, 'That plan could not be saved')
        }}
      >
        {draft && (
          <div className="space-y-3">
            <div>
              <Label htmlFor="pip-employee">Person</Label>
              <EmployeePicker
                  id="pip-employee"
                  value={draft.employeeId}
                  onChange={(employeeId) => setDraft({ ...draft, employeeId: employeeId ?? '' })}
                />
            </div>
            <div>
              <Label htmlFor="pip-reason">Why this plan is being raised</Label>
              <Input
                id="pip-reason" value={draft.reason}
                onChange={(event) => setDraft({ ...draft, reason: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="pip-expectations">What is expected</Label>
              <Input
                id="pip-expectations" value={draft.expectations}
                onChange={(event) => setDraft({ ...draft, expectations: event.target.value })}
              />
              <p className="mt-1 text-xs text-slate-600">
                Specific enough that somebody could tell afterwards whether it was met.
              </p>
            </div>
            <div>
              <Label htmlFor="pip-support">Support offered</Label>
              <Input
                id="pip-support" value={draft.supportOffered}
                onChange={(event) => setDraft({ ...draft, supportOffered: event.target.value })}
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="pip-start">From</Label>
                <DatePicker
                  id="pip-start" value={draft.startDate}
                  onChange={(event) => setDraft({ ...draft, startDate: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="pip-end">To</Label>
                <DatePicker
                  id="pip-end" value={draft.endDate}
                  onChange={(event) => setDraft({ ...draft, endDate: event.target.value })}
                />
              </div>
            </div>
          </div>
        )}
      </FormDialog>

      <FormDialog
        open={closing != null}
        onOpenChange={(open) => { if (!open) setClosing(null) }}
        title="Close this plan"
        description="Record what happened. Without it the plan cannot support whatever decision follows."
        submitLabel="Close plan"
        loading={close.isPending}
        error={error}
        onSubmit={() => {
          if (!closing) return
          act(async () => {
            await close.mutateAsync({
              id: closing.id, status: closing.status, outcomeNote: closing.outcomeNote,
            })
            setClosing(null)
          }, 'That plan could not be closed')
        }}
      >
        {closing && (
          <div className="space-y-3">
            <div>
              <Label htmlFor="close-status">Outcome</Label>
              <Select
                id="close-status"
                className="w-full"
                value={closing.status}
                onChange={(event) => setClosing({ ...closing, status: event.target.value })}
              >
                <option value="SUCCESSFUL">Met the expectations</option>
                <option value="UNSUCCESSFUL">Did not meet the expectations</option>
                <option value="CANCELLED">Cancelled</option>
              </Select>
            </div>
            <div>
              <Label htmlFor="close-note">What happened</Label>
              <Input
                id="close-note" value={closing.outcomeNote}
                onChange={(event) => setClosing({ ...closing, outcomeNote: event.target.value })}
              />
            </div>
          </div>
        )}
      </FormDialog>
    </div>
  )
}
